import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Menu, X, ShoppingBag, User } from 'lucide-react';
import { useCart } from '../lib/CartContext';
import '../styles/Museum.css';

export default function Founders() {
  const navigate = useNavigate();
  const cart = useCart();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const fadeIn = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: '-50px' },
    transition: { duration: 0.7 },
  };

  return (
    <div className="fn-page" data-testid="founders-page">
      {/* Navigation */}
      <nav className="fn-nav" data-testid="header-nav">
        <div className="fn-nav-inner">
          <div className="fn-nav-logo" onClick={() => navigate('/')} data-testid="founders-logo">
            <span className="fn-nav-logo-monogram">CN</span>
            <span className="fn-nav-logo-name">Cora's Naturals</span>
          </div>
          <div className="fn-nav-links">
            <button className="fn-nav-link" onClick={() => navigate('/')}>Shop</button>
            <button className="fn-nav-link" onClick={() => navigate('/')}>Our Blends</button>
            <button className="fn-nav-link" onClick={() => navigate('/our-story')}>Our Story</button>
            <span className="fn-nav-link fn-nav-link-active">Founder</span>
            <button className="fn-nav-link" onClick={() => navigate('/')}>Contact</button>
          </div>
          <div className="fn-nav-icons">
            <button className="fn-nav-icon" onClick={() => navigate('/admin/login')} data-testid="nav-user">
              <User size={20} strokeWidth={1.5} />
            </button>
            <button className="fn-nav-icon" onClick={() => cart.setIsOpen(true)} data-testid="nav-cart">
              <ShoppingBag size={20} strokeWidth={1.5} />
              {cart.totalItems > 0 && <span className="fn-cart-badge">{cart.totalItems}</span>}
            </button>
          </div>
          <button className="fn-hamburger" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="hamburger-btn">
            {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <motion.div className="fn-mobile-menu" initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="mobile-menu">
          <button className="fn-mobile-link" onClick={() => { setMobileMenuOpen(false); navigate('/'); }}>Shop</button>
          <button className="fn-mobile-link" onClick={() => { setMobileMenuOpen(false); navigate('/'); }}>Our Blends</button>
          <button className="fn-mobile-link" onClick={() => { setMobileMenuOpen(false); navigate('/our-story'); }}>Our Story</button>
          <span className="fn-mobile-link fn-mobile-link-active">Founder</span>
          <button className="fn-mobile-link" onClick={() => { setMobileMenuOpen(false); navigate('/'); }}>Contact</button>
        </motion.div>
      )}

      {/* Hero Section — Split Layout */}
      <section className="fn-hero" data-testid="founders-hero">
        <div className="fn-hero-photo-col">
          <img
            src="/images/founder-stephanie.jpg"
            alt="Stephanie Jones, Founder of Cora's Naturals"
            className="fn-hero-photo"
            data-testid="founder-photo"
          />
        </div>
        <div className="fn-hero-content-col">
          {/* Decorative branch SVG */}
          <svg className="fn-hero-branch" viewBox="0 0 200 400" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M100 0 C100 100 150 150 200 200 C150 220 120 260 100 400" stroke="#C4A265" strokeWidth="0.5" opacity="0.3" />
            <path d="M120 80 C140 60 170 70 180 50" stroke="#C4A265" strokeWidth="0.4" opacity="0.2" />
            <path d="M110 120 C130 100 160 110 175 90" stroke="#C4A265" strokeWidth="0.4" opacity="0.2" />
            <path d="M105 200 C125 180 155 190 170 170" stroke="#C4A265" strokeWidth="0.4" opacity="0.15" />
          </svg>

          <motion.div {...fadeIn}>
            <div className="fn-hero-meet">Meet the</div>
            <h1 className="fn-hero-founder">FOUNDER</h1>
          </motion.div>

          {/* Botanical Divider */}
          <motion.div className="fn-divider" {...fadeIn} transition={{ duration: 0.7, delay: 0.1 }}>
            <span className="fn-divider-line" />
            <svg className="fn-divider-icon" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2 C12 2 8 8 8 12 C8 16 12 18 12 22" stroke="currentColor" strokeWidth="1" />
              <path d="M12 2 C12 2 16 8 16 12 C16 16 12 18 12 22" stroke="currentColor" strokeWidth="1" />
              <path d="M6 6 C8 8 10 9 12 9" stroke="currentColor" strokeWidth="0.8" />
              <path d="M18 6 C16 8 14 9 12 9" stroke="currentColor" strokeWidth="0.8" />
              <path d="M5 14 C7 13 10 13 12 14" stroke="currentColor" strokeWidth="0.8" />
              <path d="M19 14 C17 13 14 13 12 14" stroke="currentColor" strokeWidth="0.8" />
            </svg>
            <span className="fn-divider-line" />
          </motion.div>

          <motion.div {...fadeIn} transition={{ duration: 0.7, delay: 0.2 }}>
            <h2 className="fn-founder-name">STEPHANIE JONES</h2>
            <p className="fn-founder-role">Founder of Cora's Naturals</p>
          </motion.div>

          <motion.div {...fadeIn} transition={{ duration: 0.7, delay: 0.3 }}>
            <p className="fn-bio-text">
              With 30 years of experience as a licensed cosmetologist and certification in Trichology, Stephanie Jones has dedicated her career to understanding textured hair—inside and out.
            </p>
            <p className="fn-bio-text">
              Her time behind the chair revealed a truth every stylist knows: <span className="fn-bio-highlight">different hair types and hair needs require different care.</span>
            </p>
            <p className="fn-bio-text">
              Cora's Naturals was created to provide intentional, botanical blends formulated for different hair textures, styling practices, and hair care needs.
            </p>
            <p className="fn-bio-text">
              Rooted in ancestral heritage on both sides of her family, Stephanie draws inspiration from generations of beauty traditions, botanicals, and natural remedies.
            </p>
          </motion.div>

          <motion.div className="fn-tagline" {...fadeIn} transition={{ duration: 0.7, delay: 0.4 }}>
            Professional expertise.<br />
            Ancestral inspiration.<br />
            Formulated for your texture.
          </motion.div>
        </div>
      </section>

      {/* Pillars Section */}
      <section className="fn-pillars" data-testid="founders-pillars">
        <div className="fn-pillars-inner">
          <motion.div className="fn-pillar" {...fadeIn} transition={{ duration: 0.6, delay: 0 }} data-testid="pillar-0">
            <img src="/images/pillar-experience.jpg" alt="" className="fn-pillar-icon-img" />
            <h3 className="fn-pillar-title">30 Years<br />of Experience</h3>
            <div className="fn-pillar-rule" />
            <p className="fn-pillar-text">Licensed cosmetologist with three decades of hands-on experience caring for textured hair.</p>
          </motion.div>

          <motion.div className="fn-pillar" {...fadeIn} transition={{ duration: 0.6, delay: 0.1 }} data-testid="pillar-1">
            <img src="/images/pillar-expertise.jpg" alt="" className="fn-pillar-icon-img" />
            <h3 className="fn-pillar-title">Hair & Scalp<br />Expertise</h3>
            <div className="fn-pillar-rule" />
            <p className="fn-pillar-text">Certified in Trichology with advanced knowledge of the hair, scalp, and their unique needs.</p>
          </motion.div>

          <motion.div className="fn-pillar" {...fadeIn} transition={{ duration: 0.6, delay: 0.2 }} data-testid="pillar-2">
            <img src="/images/pillar-heritage.jpg" alt="" className="fn-pillar-icon-img" />
            <h3 className="fn-pillar-title">Rooted in<br />Heritage</h3>
            <div className="fn-pillar-rule" />
            <p className="fn-pillar-text">Inspired by ancestral traditions on both sides of her family—beauty, botanicals, and natural remedies.</p>
          </motion.div>

          <motion.div className="fn-pillar" {...fadeIn} transition={{ duration: 0.6, delay: 0.3 }} data-testid="pillar-3">
            <img src="/images/pillar-texture.jpg" alt="" className="fn-pillar-icon-img" />
            <h3 className="fn-pillar-title">Created for<br />Your Texture</h3>
            <div className="fn-pillar-rule" />
            <p className="fn-pillar-text">Distinct botanical blends formulated for different hair types, styling practices, and hair care needs.</p>
          </motion.div>
        </div>
      </section>

      {/* Footer Banner */}
      <footer className="fn-footer-banner" data-testid="founders-footer">
        <div className="fn-footer-banner-inner">
          <div className="fn-footer-text">
            <p className="fn-footer-headline">
              30 Years of Expertise. <span className="fn-footer-headline-copper">Generations of Inspiration.</span>
            </p>
            <p className="fn-footer-cursive">Formulated for your texture.</p>
          </div>
          <div className="fn-footer-logo">
            <span className="fn-footer-logo-monogram">CN</span>
            <span className="fn-footer-logo-name">Cora's Naturals</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
